import { parseCSV } from '../../CSVUtils';

const CSV_EXTS = ['csv'];
const XLSX_EXTS = ['xlsx', 'xls'];

const getExtension = (filename: string): string => {
    const idx = filename.lastIndexOf('.');
    return idx === -1 ? '' : filename.slice(idx + 1).toLowerCase();
};

const sanitizeHeaders = (raw: unknown[]): string[] => {
    let cells = raw.map(c => (c == null ? '' : String(c)).trim());
    while (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
    if (cells.length === 0) {
        throw new Error('File has no header row.');
    }
    if (cells.some(c => c === '')) {
        throw new Error('Header row contains empty cells.');
    }
    const seen = new Set<string>();
    for (const name of cells) {
        if (seen.has(name)) {
            throw new Error(`Duplicate column header: "${name}".`);
        }
        seen.add(name);
    }
    return cells;
};

const readCsvHeaders = (file: File): Promise<string[]> =>
    new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
            try {
                const text = typeof fr.result === 'string' ? fr.result : '';
                if (!text) {
                    reject(new Error('File is empty.'));
                    return;
                }
                const rows = parseCSV(text);
                if (!rows || rows.length === 0) {
                    reject(new Error('File is empty.'));
                    return;
                }
                resolve(sanitizeHeaders(rows[0]));
            } catch (e) {
                reject(e);
            }
        };
        fr.onerror = () => reject(new Error('Failed to read file.'));
        fr.onabort = () => reject(new Error('File read was aborted.'));
        fr.readAsText(file);
    });

const readXlsxHeaders = async (file: File): Promise<string[]> => {
    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    if (workbook.SheetNames.length > 1) {
        throw new Error('Multi-sheet workbooks are not supported.');
    }
    if (workbook.SheetNames.length === 0) {
        throw new Error('Workbook contains no sheets.');
    }
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
    if (!rows || rows.length === 0) {
        throw new Error('Sheet is empty.');
    }
    return sanitizeHeaders(rows[0] as unknown[]);
};

export const readHeaders = async (file: File): Promise<string[]> => {
    const ext = getExtension(file.name);
    if (CSV_EXTS.includes(ext)) return readCsvHeaders(file);
    if (XLSX_EXTS.includes(ext)) return readXlsxHeaders(file);
    throw new Error('Unsupported file type. Use CSV or XLSX.');
};
